import React, { useState, useEffect } from 'react';
import { IoIosMenu } from "react-icons/io";
import { GoSun } from "react-icons/go";
import { FaMoon } from "react-icons/fa";
import { FaSearch, FaBell } from "react-icons/fa";
import { useTheme } from '../../context';
import { FiX } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { logout } from '../../redux/slices/authSlice';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useClickOutside from '../../hooks/useClickOutside';
import { Activity, LogOut, Settings, User } from 'lucide-react';
import { LocalStorage } from '../../types/local_storage.enum';

interface HeaderProps {
  sidebarDisplay: boolean;
  changeSidebarDisplay: () => void;
  isAdsVisible?: boolean;
  withMenuIcon?: boolean;
  isFixed?: boolean;
}

const Header: React.FC<HeaderProps> = ({ sidebarDisplay, withMenuIcon = true, isFixed = true, changeSidebarDisplay, isAdsVisible }) => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const { theme, toggleTheme } = useTheme();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMenuIconVisible, setIsMenuIconVisible] = useState(withMenuIcon);
  const dispatch = useAppDispatch();
  const handleSearchToggle = () => setIsSearchOpen(!isSearchOpen);
  const handleProfileToggle = () => setIsProfileOpen(!isProfileOpen);
  const profileRef = React.useRef<HTMLDivElement | null>(null);
  useClickOutside(profileRef, () => {
    if (isProfileOpen) setIsProfileOpen(false);
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMenuIconVisible(window.innerWidth <= 1279 || !!isAdsVisible);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const handleLogout = () => {
    try {
      localStorage.removeItem(LocalStorage.LOCAL_INTERVIEWD_LIST)
      dispatch(logout())
      navigate('/login')
    } catch (error) {
      toast.error((error as { message: string }).message || "Some thing went to wrong please try again")
    }
  }



  return (
    <header
      className={`${isFixed ? "fixed top-0 left-0 z-[1100] border border-red-600" : ""}   w-full h-6-vh-safe sm:h-8-vh-safe  min-h-6-vh-safe  border-b dark:border-slate-600/50  border-slate-300/50 py-2  px-3 sm:px-7 flex items-center justify-between transition-all duration-500 ease-in-out  ${theme === "dark" ? "dark  bg-black" : " bg-white"
        }`}
    >
      <div className={`flex items-center gap-2 sm:gap-6 ${withMenuIcon ? "" : "pl-2 sm:pl-4 md:pl-6 lg:pl-10"}`}>
        {/* Menu Icon */}
        {isMenuIconVisible && withMenuIcon && (
          <button
            onClick={changeSidebarDisplay}
            disabled={!user}
            className="flex-shrink-0 w-[35px]"
          >
            {sidebarDisplay ? (
              <FiX
                style={{
                  display: !user ? "none" : "block"
                }}
                size={25}
                className={`cursor-pointer ${theme === "dark" ? "text-white" : "text-black"
                  }`}
              />
            ) : (
              <IoIosMenu
                style={{
                  display: !user ? "none" : "block"
                }}
                size={25}
                className={`cursor-pointer ${theme === "dark" ? "text-white" : "text-black"
                  }`}
              />
            )}
          </button>
        )}

        {/* Logo and Name Section */}
        <Link
          to={'/'}
          className={`flex items-center h-[50px] border-hidden shadow-inner pl-2 pr-2 sm:pr-9`}
        >
          <img src="/logo/green-logo.png" alt="LOGO" className="w-11  cursor-pointer flex-shrink-0" />
          <span
            className={` text-[16px] sm:inline hidden ${theme === "dark" ? "text-white" : "text-black"
              } ${sidebarDisplay && isMenuIconVisible ? "hidden sm:block" : "block"}`}
          >
            Sapiens AI
          </span>
        </Link>
      </div>

      <div className="flex gap-2 sm:gap-6 items-center relative">
        {/* Search Icon and Input */}
        {/* <div className="relative p-2 sm:p-0 hidden max-[470px]:hidden sm:block">
          <FaSearch
            className={`cursor-pointer ${theme === "dark" ? "text-white" : "text-black"
              } hover:text-opacity-75 active:text-opacity-100`}
            size={20}
            onClick={handleSearchToggle}
          />
          <input
            type="text"
            placeholder="Search..."
            className={`${isSearchOpen ? "w-32 sm:w-40 opacity-100" : "w-0 opacity-0"
              } absolute right-8 top-1/2 -translate-y-1/2 pl-2 pr-8 py-1 rounded-md border ${theme === "dark"
                ? "bg-gray-800 text-white border-gray-600"
                : "bg-white text-black border-gray-300"
              } focus:outline-none transition-all duration-500 ease-in-out`}
          />
        </div> */}

        {/* Notification Icon */}
        {/* <div className="p-2 sm:p-0">
          <FaBell
            className={`cursor-pointer ${theme === "dark" ? "text-white" : "text-black"
              } hover:text-opacity-75 active:text-opacity-100`}
            size={20}
          />
        </div> */}

        {/* Theme Toggle */}
        <div className="p-2 sm:p-0">
          <button onClick={toggleTheme}>
            <GoSun
              className="text-white hidden dark:inline cursor-pointer hover:text-opacity-75 active:text-opacity-100"
              size={20}
            />
            <FaMoon
              className="text-black inline dark:hidden cursor-pointer hover:text-opacity-75 active:text-opacity-100"
              size={20}
            />
          </button>
        </div>

        {/* User Profile */}
        <div className="relative">
          <button
            className="flex items-center gap-1 cursor-pointer p-2 sm:p-0"
            disabled={!user}
            style={{
              display: !user ? "none" : "block"
            }}
            onClick={handleProfileToggle}
          >
            <div className="flex justify-center items-center  rounded-full bg-gray-400 text-black hover:text-white hover:bg-gray-800">
              {
                user?.profilePicturePath ? (
                  <img src={user.profilePicturePath} alt="User" className="w-8 h-8 min-h-8 max-h-8 min-w-8 max-w-8 rounded-full" />
                ) : (
                  <span className={` rounded-full bg-accent-500 text-lg p-0 w-8 h-8 sm:h-[2rem] sm:w-[2rem] flex items-center justify-center text-white`}>
                    {user?.firstName?.[0] || "U"}
                  </span>
                )
              }
            </div>
          </button>

          {/* Profile Dropdown Menu */}
          {isProfileOpen && (
            <div
              ref={profileRef}
              className="absolute right-0 top-6 bg-white dark:bg-gray-900 shadow-xl rounded-xl w-52 p-1 z-[5000] transition-all duration-300 border border-gray-200 dark:border-gray-700"
            >
              {/* User info section with avatar and better typography */}
              <div className="p-4 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-accent-400 to-accent-500 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-900 dark:text-gray-100 font-medium text-sm">{user?.firstName}</p>
                    <div className="flex items-center space-x-1 mt-1">
                      <Activity className="w-3 h-3 text-green-500" />
                      <span className="text-green-500 text-xs font-medium">Active</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-1">
                <a
                  href="/profile"
                  className="flex items-center space-x-3 w-full px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
                >
                  <Settings className="w-4 h-4" />
                  <span>Profile Settings</span>
                </a>

                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-3 w-full px-3 py-2.5 text-sm text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-200 mt-1"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;